import { useMemo } from 'react';
import { Card } from '../../common/Card';
import { AnimatePresence, motion } from 'framer-motion';
import { getServiceTypeLabel } from '../../../data/gisServices';

const listVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 }
};

export const ServiceControl = ({
  categories,
  activeCategories,
  onToggleCategory,
  services,
  activeServiceId,
  onServiceSelect,
  searchQuery,
  onSearchChange,
  showServices,
  onToggleShowServices,
  totalCount,
  availableTypes,
  activeTypes,
  onToggleType
}) => {
  const categoryMap = useMemo(() => {
    return categories.reduce((acc, category) => {
      acc[category.id] = category;
      return acc;
    }, {});
  }, [categories]);

  const activeTypeCount = activeTypes.length;

  return (
    <Card className="p-4 space-y-4 map-panel-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span>🛰️</span>
          <h3 className="text-lg font-black">خدمات الخرائط</h3>
        </div>
        <label className="flex items-center gap-2 text-xs font-semibold">
          <input
            type="checkbox"
            checked={showServices}
            onChange={() => onToggleShowServices(!showServices)}
            className="w-4 h-4"
          />
          عرض الخدمات
        </label>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
        <div className="map-soft-box">
          إجمالي الخدمات: <span className="number font-bold">{totalCount}</span>
        </div>
        <div className="map-soft-box">
          فلاتر النوع: <span className="number font-bold">{activeTypeCount}</span>
        </div>
      </div>

      <div className="space-y-2">
        {categories.map((category) => (
          <label
            key={category.id}
            className="map-choice-row"
          >
            <input
              type="checkbox"
              checked={activeCategories.includes(category.id)}
              onChange={() => onToggleCategory(category.id)}
              className="w-5 h-5 mt-1"
            />
            <div className="flex-1 text-right space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-xl">{category.icon}</span>
                <p className="font-semibold">{category.name}</p>
              </div>
              <p className="text-xs text-gray-500">{category.description}</p>
            </div>
            <div
              className="w-4 h-4 rounded-full mt-1"
              style={{ backgroundColor: category.color }}
            />
          </label>
        ))}
      </div>

      <input
        type="text"
        value={searchQuery}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder="ابحث عن خدمة خرائط..."
        className="input-text h-10 text-sm"
      />

      <details className="rounded-2xl border border-sky-100 bg-white/70 p-3 group">
        <summary className="cursor-pointer text-sm font-semibold text-gray-700 flex items-center justify-between">
          <span>فلترة متقدمة</span>
          <span className="text-[10px] text-gray-500">{activeTypeCount} فلتر</span>
        </summary>
        <div className="mt-3 space-y-2">
          <p className="text-xs font-semibold text-gray-600">حسب نوع الخدمة</p>
          <div className="flex flex-wrap gap-2">
            {availableTypes.map((type) => {
              const isActive = activeTypes.includes(type);
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => onToggleType(type)}
                  className={`map-filter-pill ${isActive ? 'map-filter-pill-active' : ''}`}
                >
                  {getServiceTypeLabel(type)}
                </button>
              );
            })}
          </div>
        </div>
      </details>

      <div className="flex items-center justify-between text-xs font-semibold text-gray-600">
        <span>النتائج</span>
        <span className="number">{services.length}</span>
      </div>

      <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
        {services.length === 0 ? (
          <p className="text-sm text-gray-500">
            اختر مجموعة خدمات لعرضها على الخريطة.
          </p>
        ) : (
          <AnimatePresence mode="popLayout">
            {services.map((service) => (
              <motion.div
                layout
                key={service.id}
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={listVariants}
                transition={{ duration: 0.2 }}
                className={`rounded-2xl p-3 space-y-2 transition border ${
                  activeServiceId === service.id
                    ? 'border-cyan-300 bg-cyan-50/70'
                    : 'border-sky-100 bg-white'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 text-right space-y-1">
                    <p className="text-sm font-semibold">{service.name}</p>
                    <p className="text-xs text-gray-500">
                      {getServiceTypeLabel(service.type)} · {categoryMap[service.category]?.name}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    <button
                      type="button"
                      onClick={() => onServiceSelect(service)}
                      className="map-mini-btn"
                    >
                      الذهاب للمكان
                    </button>
                    <a
                      href={service.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[10px] font-semibold text-sky-700 underline"
                    >
                      فتح الرابط
                    </a>
                  </div>
                </div>
                <p className="text-[11px] text-gray-600 leading-snug">
                  {service.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="map-filter-pill">{getServiceTypeLabel(service.type)}</span>
                  <span className="map-filter-pill">{categoryMap[service.category]?.name}</span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>
    </Card>
  );
};
